/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useState, useRef, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import PushPanel from '../../PushPanel/PushPanel.js';

var Context = /*#__PURE__*/createContext();
Context.displayName = 'HelpPanelContext';
var Provider = function Provider(_ref) {
  var children = _ref.children,
    hideFromDom = _ref.hideFromDom;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    content = _useState2[0],
    setContent = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    open = _useState4[0],
    setOpen = _useState4[1];
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var resetTimeout = useRef();
  var reset = useCallback(function () {
    setOpen(false);

    // Let the PushPanel animate out before clearing the content
    clearTimeout(resetTimeout.current);
    resetTimeout.current = setTimeout(function () {
      setContent();
    }, 500);
  }, []);
  var show = useCallback(function (panelContent) {
    clearTimeout(resetTimeout.current);
    if (panelContent) {
      setContent(panelContent);
    }
    setOpen(true);
  }, []);
  var hide = useCallback(function () {
    reset();
  }, [reset]);
  var value = useMemo(function () {
    return {
      show: show,
      hide: hide,
      setContent: setContent
    };
  }, [hide, show]);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: value
  }, (!!hideFromDom && open || !hideFromDom) && /*#__PURE__*/React.createElement(PushPanel, {
    open: open,
    onClose: hide,
    panelTitle: t('Help'),
    isHelp: true
  }, content || /*#__PURE__*/React.createElement("div", null)), children);
};
Provider.propTypes = {
  children: PropTypes.node.isRequired,
  hideFromDom: PropTypes.bool
};
var useHelpPanel = function useHelpPanel() {
  return useContext(Context);
};
var HelpPanel = {
  Context: Context,
  Provider: Provider,
  useHelpPanel: useHelpPanel
};

export { HelpPanel as default };
